// P2b tanda 3e (ajuste 8) — llevar una foto a proporción 4:5 ANTES de subir
// (y antes de la conversión a WebP y la compresión). Dos caminos: RECORTE (un
// marco 4:5 móvil y escalable, centrado por defecto) o MARGEN (rellenar hasta
// 4:5 con el color del fondo detectado en los bordes). Acá viven las piezas
// PURAS y testeables; el canvas (recortar/pintar de verdad) va en el selector.
// Exactitud: todos los rectángulos se construyen con alto múltiplo de 5 y
// ancho = alto·4/5, así la proporción es EXACTA en píxeles enteros.

// Marco de recorte 4:5 dentro de la foto. cx/cy = centro del marco como
// fracción (0..1) de la foto; escala = tamaño relativo al marco máximo (0..1).
// SIEMPRE devuelve un rect exacto 4:5 completamente dentro de la foto.
export function rectDeRecorte(w, h, { cx = 0.5, cy = 0.5, escala = 1 } = {}) {
  const baseH = Math.floor(Math.min(h, (w * 5) / 4) / 5) * 5;
  const esc = Math.min(Math.max(Number(escala) || 1, 0.1), 1);
  let fh = Math.floor((baseH * esc) / 5) * 5;
  if (fh < 5) fh = 5;
  const fw = (fh / 5) * 4;
  let x = Math.round(cx * w - fw / 2);
  let y = Math.round(cy * h - fh / 2);
  x = Math.min(Math.max(0, x), w - fw);
  y = Math.min(Math.max(0, y), h - fh);
  return { x, y, width: fw, height: fh };
}

// El marco por defecto: el más grande posible, centrado.
export function recorteCentrado45(w, h) {
  return rectDeRecorte(w, h);
}

// Lienzo 4:5 mínimo que contiene la foto ENTERA sin deformarla, con la foto
// centrada (dx/dy = margen izquierdo/superior). El relleno lo pinta el caller.
export function margenA45(w, h) {
  let width, height;
  if (w / h <= 4 / 5) {
    height = Math.ceil(h / 5) * 5;
    width = (height / 5) * 4;
  } else {
    width = Math.ceil(w / 4) * 4;
    height = (width / 4) * 5;
  }
  return { width, height, dx: Math.floor((width - w) / 2), dy: Math.floor((height - h) / 2) };
}

// P3-1 (punto 4) — ENCUADRE unificado: un solo modelo con acercar/alejar y
// mover sobre un lienzo 4:5 fijo. zoom=1 = la foto entera dentro del lienzo
// (como el margen de la 3e); zoom>1 acerca (recorta por los bordes); zoom<1
// aleja (más margen). cx/cy = dónde queda el CENTRO de la foto como fracción
// del lienzo (0.5/0.5 = centrada). El caller pinta el fondo y dibuja la foto
// en {dx,dy,dw,dh}. El lienzo se acota a maxW×maxH (la compresión final igual
// redimensiona; un PNG intermedio gigante solo gasta memoria).
// Reemplaza al par recorte/margen de la 3e COMO INTERFAZ (las piezas viejas
// quedan para el test de exactitud); la vista previa se dibuja en un CANVAS
// real — el marco absoluto por porcentajes + box-shadow de 400px de la 3e es
// la clase del defecto visual medido (bandas sin oscurecer, marco corrido).
export function encuadre45(w, h, { zoom = 1, cx = 0.5, cy = 0.5, maxW = 1600, maxH = 2000 } = {}) {
  const base = margenA45(w, h);
  const k = Math.min(1, maxW / base.width, maxH / base.height);
  const canvasW = Math.round(base.width * k);
  const canvasH = Math.round((canvasW / 4) * 5);
  const z = Math.min(Math.max(Number(zoom) || 1, 0.25), 4);
  const dw = w * k * z;
  const dh = h * k * z;
  const fx = Math.min(Math.max(Number(cx) || 0.5, 0), 1);
  const fy = Math.min(Math.max(Number(cy) || 0.5, 0), 1);
  return {
    canvasW, canvasH,
    dx: Math.round(fx * canvasW - dw / 2),
    dy: Math.round(fy * canvasH - dh / 2),
    dw: Math.round(dw), dh: Math.round(dh),
  };
}

// Color de fondo detectado: promedio de los píxeles del BORDE (una foto de
// producto sobre fondo liso tiene el fondo en los bordes; el producto, en el
// centro, no contamina). data = RGBA plano (como ImageData.data).
export function colorDeBordes(data, width, height) {
  let r = 0, g = 0, b = 0, n = 0;
  const sumar = (x, y) => {
    const i = (y * width + x) * 4;
    r += data[i]; g += data[i + 1]; b += data[i + 2]; n += 1;
  };
  for (let x = 0; x < width; x++) { sumar(x, 0); if (height > 1) sumar(x, height - 1); }
  for (let y = 1; y < height - 1; y++) { sumar(0, y); if (width > 1) sumar(width - 1, y); }
  if (!n) return '#ffffff';
  const hex = (v) => Math.round(v / n).toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}
